import {
  Address,
  ApplicationFileGeneratorToolFieldsFragment,
  CaraWorkspaceArtifactType,
  CaraWorkspaceFieldsFragment,
  DocxEditorToolFieldsFragment,
  FillTemplateToolFieldsFragment
} from '@oysterjs/ais/api/graphql';

/** Fallback user message when the text box is empty but library items are selected. */
export function buildCaraFallbackMessage(parts: {
  customFormIds: string[];
  htmlTemplateIds: string[];
  wordTemplateIds: string[];
  excelTemplateIds?: string[];
}): string {
  const sentences: string[] = [];
  if (parts.customFormIds.length > 0) {
    sentences.push('Fill the selected custom form(s) using the attached source documents.');
  }
  if (parts.htmlTemplateIds.length > 0 || parts.wordTemplateIds.length > 0) {
    sentences.push('Use the selected template(s) to generate a document.');
  }
  if ((parts.excelTemplateIds?.length ?? 0) > 0) {
    sentences.push('Use the selected Excel template(s) to generate a spreadsheet.');
  }
  return sentences.join(' ');
}

export const titleCase = (s: string): string =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0]?.toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

export const titleCaseFromCamelCase = (s: string): string =>
  s
    // Handle consecutive uppercase letters (acronyms)
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    // Handle camelCase transitions
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    // Handle numbers
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();

export const renderAddress = (address: Address): string => {
  if (!address.line1) {
    return '';
  }
  return `${address.line1}, ${address.city}, ${address.zone}, ${address.postalCode}`;
};

const COMPANY_SUFFIX_PATTERN =
  /\b(corp|corp\.|corporation|inc|inc\.|incorporated|ltd|ltd\.|limited|llc)\b/gi;

/**
 * Generate a sensible template title from a source document's title.
 * Strips common company suffixes (e.g. "Acme Corp Proposal" -> "Acme Proposal Template")
 * and falls back to "Template" when no source title is available.
 */
export const buildTemplateTitleFromDocumentTitle = (
  sourceTitle: string | null | undefined
): string => {
  if (!sourceTitle) {
    return 'Template';
  }
  const stripped = sourceTitle.replace(COMPANY_SUFFIX_PATTERN, '').trim();
  return `${stripped} Template`;
};

export function isDocxApplicationFile(params: {
  artifact?: CaraWorkspaceFieldsFragment['artifacts'][0];
  toolCall?: ApplicationFileGeneratorToolFieldsFragment;
}): boolean {
  const { artifact, toolCall } = params;
  return (
    artifact?.type === CaraWorkspaceArtifactType.DocxFile ||
    artifact?.applicationFile?.type?.toUpperCase() === 'DOCX' ||
    toolCall?.input?.fileType?.toUpperCase() === 'DOCX'
  );
}

export function applicationFileGeneratingLabel(isDocx: boolean, ellipsis: boolean): string {
  const label = isDocx ? 'Word document' : 'Spreadsheet';
  return ellipsis ? `${label}...` : label;
}

/** Human-readable title from a DocxEditor tool call (top-level `input.title`). */
export function docxEditorToolTitle(
  toolCall: DocxEditorToolFieldsFragment | null | undefined
): string | undefined {
  const title = toolCall?.input?.title?.trim();
  return title || undefined;
}

/**
 * Extract a human-readable client/insured name from any ACORD-shaped fill-template
 * tool input. Returns an empty string when no recognizable name field is populated.
 */
export const extractClientNameFromFillTemplateInput = (
  input: FillTemplateToolFieldsFragment['input'] | null | undefined
): string => {
  return input?.templateId?.replaceAll('_', ' ').trim() || '';
};

/** Shown on a filled PDF form card when no form name can be resolved. */
export const FILLED_FORM_FALLBACK_TITLE = 'Filled Form';

/**
 * Resolve a custom form's display name from the PDF editor LIST and DESCRIBE results already
 * loaded with the workspace. A FILL result identifies its form only by ID, so the name has to
 * come from the LIST or DESCRIBE call that preceded it in the LIST -> DESCRIBE -> FILL workflow.
 */
export function findCustomFormName(
  workspace: CaraWorkspaceFieldsFragment,
  formId: string | null | undefined
): string | undefined {
  if (!formId) {
    return undefined;
  }
  for (const stage of workspace.planStages) {
    for (const stageEvent of stage.events) {
      const output = stageEvent.toolCall?.pdfEditorTool?.output;
      if (!output) {
        continue;
      }
      const described = output.form?.id === formId ? output.form.name?.trim() : undefined;
      if (described) {
        return described;
      }
      const listed = output.forms?.find((form) => form.id === formId)?.name?.trim();
      if (listed) {
        return listed;
      }
    }
  }
  return undefined;
}

/**
 * Build the card title for a filled PDF form. The form's own name leads the label so that cards
 * for different forms produced in the same turn differ from their first characters, and the chain
 * degrades to a generic label rather than rendering blank when nothing identifies the form.
 */
export function pdfEditorFillTitle(params: {
  artifact?: CaraWorkspaceFieldsFragment['artifacts'][0];
  formName?: string | null;
  inputFileName?: string | null;
}): string {
  const { artifact, formName, inputFileName } = params;
  return (
    artifact?.customForm?.title?.trim() ||
    artifact?.customForm?.customForm?.title?.trim() ||
    artifact?.pdf?.title?.trim() ||
    formName?.trim() ||
    inputFileName?.trim() ||
    FILLED_FORM_FALLBACK_TITLE
  );
}
